import { useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import { type UseFormReturn, useForm } from '@kanda-libs/ks-component-ts';
import {
  ModalsWrapperContext,
  StringIndexedObject,
  useToast,
} from '@kanda-libs/ks-design-library';
import { actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import { selectors } from 'store';

import { REQUEST_CHANGES_MODAL_ID } from './constants';

export interface RequestChangesModalFormValues {
  reason: string;
}

export interface RequestChangesModalFormHook {
  form: UseFormReturn<RequestChangesModalFormValues>;
  onSubmit: (
    formValues: StringIndexedObject<RequestChangesModalFormValues>,
  ) => void;
  isSubmitting: boolean;
}

export default function useRequestChangesModalForm(): RequestChangesModalFormHook {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const { id } = useSelector(selectors.getPathKey);
  const { hideModal } = useContext(ModalsWrapperContext);

  const { showError, showSuccess } = useToast();

  const form = useForm<RequestChangesModalFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const handleError = useCallback(
    (message: string | undefined) => {
      showError(message ?? 'Unknown error encountered');
      setIsSubmitting(false);
      hideModal(REQUEST_CHANGES_MODAL_ID);
    },
    [showError, hideModal],
  );

  const handleSuccess = useCallback(() => {
    showSuccess('Job changes request submitted');
    setIsSubmitting(false);
    hideModal(REQUEST_CHANGES_MODAL_ID);
  }, [showSuccess, hideModal]);

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<RequestChangesModalFormValues>) => {
      if (!id) return;
      const { reason } = formValues as unknown as RequestChangesModalFormValues;
      if (!reason) return;
      setIsSubmitting(true);
      dispatch(
        actions.stepJob({
          params: {
            id,
          },
          body: {
            name: 'checkout_quote_changes_requested',
            text: reason,
            appended_at: new Date(),
          },
          onError: (error) => handleError(error.message),
          onSuccess: () => handleSuccess(),
        }),
      );
    },
    [id, dispatch, handleError, handleSuccess],
  );

  return {
    form,
    onSubmit,
    isSubmitting,
  };
}
